import { ChangeDetectionStrategy, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TealiumUtagService } from '@woolworthsnz/analytics';
import { DynamicContentService, DynamicContentTargets, WithDynamicContent } from '@woolworthsnz/dynamic-content';
import { STORE_LOCATOR_PATH } from '@woolworthsnz/shop';
import {
	Division,
	FacilityDTO,
	generateBreadcrumb,
	getServices,
	Site,
	SiteDetail,
	StoreBreadcrumbService,
	StoresService,
	TradingHours,
	PharmacyComponent,
	DetailsServiceComponent,
	GoogleStoresMapComponent,
	DetailsHeaderComponent,
	SeoComponent,
} from '@woolworthsnz/store-locator';
import {
	AppSettingsService,
	BreadcrumbComponent,
	BreakPointService,
	CustomWindow,
	MetaDataService,
	WINDOW,
	parseStringToIntOrDefault,
} from '@woolworthsnz/styleguide';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { NgIf, NgClass, NgTemplateOutlet, AsyncPipe } from '@angular/common';

@WithDynamicContent()
@Component({
	selector: 'wnz-store-details',
	templateUrl: './store-details.page.html',
	styleUrls: ['./store-details.page.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		SeoComponent,
		NgClass,
		BreadcrumbComponent,
		NgTemplateOutlet,
		DetailsHeaderComponent,
		GoogleStoresMapComponent,
		DetailsServiceComponent,
		PharmacyComponent,
		AsyncPipe,
	],
})
export class StoreDetailsPage implements OnInit, OnDestroy {
	@ViewChild('dynamicContentPanel') dynamicContentPanel: ElementRef;
	storeId?: number;
	apiKey = '';
	seoImage: string;
	showBreadcrumb?: boolean;

	errors$ = new BehaviorSubject(false);
	siteDetail$ = new BehaviorSubject<SiteDetail | undefined>(undefined);
	site$ = new BehaviorSubject<Site | undefined>(undefined);
	tradingHours$ = new BehaviorSubject<TradingHours[]>([]);
	liquorTradingHours$ = new BehaviorSubject<TradingHours[]>([]);
	services$ = new BehaviorSubject<FacilityDTO[]>([]);
	isMobile$: Observable<boolean>;

	pharmacyStore$ = new BehaviorSubject<Site | undefined>(undefined);
	pharmacyTradingHours$ = new BehaviorSubject<TradingHours[]>([]);
	private destroyed$: Subject<boolean> = new Subject();

	constructor(
		private route: ActivatedRoute,
		private appSettingsService: AppSettingsService,
		private dynamicContentService: DynamicContentService,
		private storeService: StoresService,
		private breakpointService: BreakPointService,
		private metaDataService: MetaDataService,
		private breadcrumbService: StoreBreadcrumbService,
		private tealiumUtagService: TealiumUtagService,
		@Inject(WINDOW) protected window: CustomWindow
	) {
		this.apiKey = this.appSettingsService.getSetting('googleApiKey');
		this.seoImage = this.appSettingsService.getMetaImage('storeLocator');
	}

	ngOnInit(): void {
		this.dynamicContentService.setViewChildForOutlet(
			this.dynamicContentPanel,
			DynamicContentTargets.dynamicContentPanel
		);

		this.showBreadcrumb = this.route.snapshot.queryParamMap.get('breadcrumb')
			? this.route.snapshot.queryParamMap.get('breadcrumb')?.toLowerCase() === 'true'
			: true;

		this.route.paramMap.pipe(takeUntil(this.destroyed$)).subscribe((paramMap) => {
			this.storeId = parseStringToIntOrDefault(paramMap.get('storeId'), 0);
			this.fetchStore();
		});

		this.isMobile$ = this.breakpointService.isSmallDevice$;
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
		this.dynamicContentService.clearViewChildForOutlet(DynamicContentTargets.dynamicContentPanel);
	}

	fetchStore(): void {
		if (this.storeId === undefined) {
			return;
		}

		this.storeService
			.getStoreInfo(this.storeId)
			.pipe(
				takeUntil(this.destroyed$),
				catchError((err) => {
					this.errors$.next(true);
					return throwError(err);
				})
			)
			.subscribe((stores) => {
				const store = stores.find((s) => s.site?.division === Division.countdown);

				if (!store) {
					return;
				}

				this.siteDetail$.next(store);

				if (store.site) {
					this.metaDataService.setPageMeta(
						store.site.name ?? this.appSettingsService.getPageTitle('storeLocator'),
						store.site.name ?? this.appSettingsService.getPageTitle('storeLocator'),
						this.appSettingsService.getMetaDescription('default'),
						this.appSettingsService.getMetaImage('default'),
						this.appSettingsService.getMetaUrl('default')
					);
					this.site$.next(store.site);
				}
				if (store.tradingHours) {
					this.tradingHours$.next(store.tradingHours);
				}
				if (store.site?.facilityList?.facility) {
					this.services$.next(getServices(store.site.facilityList.facility));
				}
				if (store.site?.name) {
					this.breadcrumbService.setBreadcrumbs(
						generateBreadcrumb(
							`/${STORE_LOCATOR_PATH}/search`,
							this.route.snapshot.queryParamMap,
							this.storeId || 0,
							store.site.name,
							STORE_LOCATOR_PATH
						)
					);
				}

				const pharmacy = stores.find((s) => s.site?.division === Division.countdownPharmacy);

				if (pharmacy) {
					if (pharmacy.site) {
						this.pharmacyStore$.next(pharmacy.site);
					}
					if (pharmacy.tradingHours) {
						this.pharmacyTradingHours$.next(pharmacy.tradingHours);
					}
				}

				const liquorHours = stores.find((s) => s.site?.division === Division.countdownLiquor)?.tradingHours;

				if (liquorHours) {
					this.liquorTradingHours$.next(liquorHours);
				}
				this.trackDetailsView(store.site?.name);
			});
	}

	trackDetailsView(storename?: string | null): void {
		this.tealiumUtagService.view({
			tealium_event: 'nav_interaction',
			interaction_source: 'page',
			tealium_event_label: 'store details',
			nav_type: 'Search',
			nav_sub_type: 'Store Details',
			nav_value: storename ?? '',
		});
	}

	handleDirectionsClick(url: string): void {
		const searchString = this.route.snapshot.queryParamMap.get('q') || '';
		const searchResultsCount = this.route.snapshot.queryParamMap.get('count') || 0;

		this.tealiumUtagService.view({
			tealium_event: 'nav_interaction',
			interaction_source: 'page',
			nav_type: 'Search',
			nav_sub_type: 'Store Directions',
			nav_value: searchString,
			nav_results_returned: searchResultsCount,
		});

		this.window.open(url, '_blank');
	}
}
